import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogActions from '@material-ui/core/DialogActions'
import { FiDownload } from 'react-icons/fi'
import { toast } from 'react-toastify'
import { downloadMissionsPerExpert, readBlobError } from '../../store/action/reporting'

// Mêmes styles que le dialogue « Nouveau sinistre » (AddClaimDialog) pour une présentation homogène
const useStyles = makeStyles(() => ({
  openButton: {
    fontFamily: 'Poppins',
    fontSize: '10.77px',
    fontWeight: 600,
    lineHeight: '16.16px',
    color: 'rgba(255, 255, 255, 1)',
    background: 'rgb(186, 213, 58)',
    width: '140px',
    height: '30px',
    borderRadius: '5.39px',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    marginLeft: '15px',
  },
  dialogPaper: { overflow: 'hidden' },
  dialogTitle: {
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: 14,
    color: 'rgba(63, 66, 84, 1)',
    backgroundColor: 'rgba(243, 246, 249, 1)',
    borderBottom: '1px solid rgba(243, 246, 249, 1)',
    padding: '10px 16px',
    position: 'relative',
  },
  dialogActions: {
    backgroundColor: 'rgba(243, 246, 249, 1)',
    padding: '10px 16px',
    gap: 8,
  },
  content: { backgroundColor: 'white', padding: '16px 16px 20px' },
  titreLabel: {
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: 13,
    color: 'rgba(63, 66, 84, 1)',
    marginBottom: 6,
  },
  help: {
    fontFamily: 'Poppins',
    fontSize: 12,
    lineHeight: '18px',
    color: 'rgba(70, 78, 95, 1)',
    marginBottom: 16,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
  },
  modalLabel: {
    fontFamily: 'Poppins',
    fontSize: 12,
    fontWeight: 500,
    lineHeight: '18px',
    marginBottom: 4,
    color: 'rgba(70, 78, 95, 1)',
    display: 'block',
  },
  selectInputDialog: {
    width: '100%',
    boxSizing: 'border-box',
    height: 34,
    padding: '0 10px',
    border: '1px solid transparent',
    borderRadius: 5,
    background: 'rgba(243, 246, 249, 1)',
    fontFamily: 'Poppins',
    fontSize: 12,
    color: 'rgba(63, 66, 84, 1)',
    outline: 'none',
    '&:focus': { borderColor: 'rgba(187, 200, 8, 1)', background: 'white' },
    '&:disabled': { opacity: 0.6 },
  },
  progress: {
    marginTop: 14,
    fontFamily: 'Poppins',
    fontSize: 12,
    color: 'rgba(60, 101, 86, 1)',
  },
  CancelStyle: {
    height: 32,
    padding: '0 18px',
    color: 'rgb(255, 54, 54)',
    backgroundColor: 'rgb(255, 229, 229)',
    borderRadius: 5,
    fontFamily: 'Poppins',
    fontSize: 12,
    fontWeight: 600,
    border: 0,
    cursor: 'pointer',
    '&:disabled': { opacity: 0.6, cursor: 'default' },
  },
  ajouterStyle: {
    height: 32,
    minWidth: 90,
    padding: '0 18px',
    backgroundColor: 'rgba(187, 200, 8, 1)',
    color: 'rgba(255, 255, 255, 1)',
    borderRadius: 5,
    fontFamily: 'Poppins',
    fontSize: 12,
    fontWeight: 600,
    border: 0,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    '&:disabled': { opacity: 0.6, cursor: 'wait' },
  },
}))

const MAX_DAYS = 366
const daysBetween = (from, to) => Math.round((new Date(to) - new Date(from)) / 86400000) + 1
// Jour local au format AAAA-MM-JJ (valeur des champs date)
const toInputDate = (d) => {
  const x = new Date(d)
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`
}
const today = () => toInputDate(new Date())
const firstDayOfMonth = () => {
  const d = new Date()
  return toInputDate(new Date(d.getFullYear(), d.getMonth(), 1))
}

/**
 * Bouton « Reporting » (à côté d'« Ajouter Sinistre ») : ouvre une fenêtre avec deux dates et télécharge le
 * classeur « missions par expert » calculé par ms-inspection (MAE). Affiché avec l'autorité VIEW_MISSION_REPORTING.
 */
function ReportingDialog() {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  // Par défaut : du 1er du mois en cours à aujourd'hui ; aucune date ne peut dépasser aujourd'hui
  const [from, setFrom] = useState(firstDayOfMonth())
  const [to, setTo] = useState(today())
  const [loading, setLoading] = useState(false)

  const close = () => {
    if (loading) return
    setOpen(false)
  }

  const validate = () => {
    if (!from || !to) return 'Saisissez la date de début et la date de fin.'
    if (from > today() || to > today()) return 'Les dates ne peuvent pas dépasser la date du jour.'
    if (new Date(to) < new Date(from)) return 'La date de fin doit être postérieure ou égale à la date de début.'
    const days = daysBetween(from, to)
    if (days > MAX_DAYS) return `La plage ne doit pas dépasser ${MAX_DAYS} jours (${days} demandés).`
    return null
  }

  const handleDownload = async () => {
    const message = validate()
    if (message) {
      toast.error(message)
      return
    }
    setLoading(true)
    try {
      const { fileName } = await downloadMissionsPerExpert({ from, to })
      toast.success(`Rapport téléchargé : ${fileName}`)
      setOpen(false)
    } catch (error) {
      console.error('[ReportingDialog]', error)
      toast.error(await readBlobError(error, 'Échec de la génération du rapport.'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <button className={classes.openButton} onClick={() => setOpen(true)}>
        <FiDownload style={{ height: '16px', width: '16px' }} />
        Export
      </button>

      <Dialog open={open} onClose={close} classes={{ paper: classes.dialogPaper }} fullWidth maxWidth="sm">
        <DialogTitle className={classes.dialogTitle}>
          <span style={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 13 }}>Reporting missions par expert</span>
          <span
            style={{ position: 'absolute', right: 16, top: 8, fontSize: 18, cursor: 'pointer', color: 'rgba(63, 66, 84, 1)' }}
            onClick={close}
          >
            ✕
          </span>
        </DialogTitle>
        <DialogContent className={classes.content}>
          <div className={classes.titreLabel}>Période (date d&apos;ouverture du sinistre)</div>
          <div className={classes.help}>
            Classeur Excel à deux onglets : total par expert (missions reçues, photos avant et après réparation, missions
            traitées) et détail par mission. Plage de 12 mois au maximum, jusqu&apos;à la date du jour.
          </div>
          <div className={classes.grid}>
            <div>
              <label className={classes.modalLabel} htmlFor="reporting-from">Date de début</label>
              <input
                id="reporting-from"
                type="date"
                className={classes.selectInputDialog}
                value={from}
                max={to && to < today() ? to : today()}
                onChange={(e) => setFrom(e.target.value)}
                disabled={loading}
              />
            </div>
            <div>
              <label className={classes.modalLabel} htmlFor="reporting-to">Date de fin</label>
              <input
                id="reporting-to"
                type="date"
                className={classes.selectInputDialog}
                value={to}
                min={from || undefined}
                max={today()}
                onChange={(e) => setTo(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
          {loading && (
            <div className={classes.progress}>
              Génération en cours, jusqu&apos;à une minute sur une année complète. Ne fermez pas la fenêtre.
            </div>
          )}
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <button className={classes.CancelStyle} onClick={close} disabled={loading}>
            Annuler
          </button>
          <button className={classes.ajouterStyle} onClick={handleDownload} disabled={loading}>
            <FiDownload />
            {loading ? 'Génération en cours...' : 'Télécharger'}
          </button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ReportingDialog
